import React, { FC, useEffect } from 'react';
import classNames from 'classnames';
import useFocus from '../instant-offers/utils/use-focus';
import { nanoid } from 'nanoid';
import './TextField.scss';

import { AUTOCOMPLETE, INPUTMODE, INPUT_TYPES } from '../instant-offers/utils/constants';

interface TextFieldProps {
    /**
     * Controls whether and how the text value should be automatically capitalized as it is entered/edited by the user
     *
     * @default none
     */
    autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';

    /**
     * Provide a hint to the user-agent as to the expected nature of the user input, ideally allowing more sensible autocompletion
     */
    autoComplete?: AUTOCOMPLETE;

    /**
     * Additional CSS class(es) to add to the `<TextField />`
     */
    className?: string;

    /**
     * Attribute indicating the user cannot interact with the `<TextField />`
     *
     * @default false
     */
    disabled?: boolean;

    /**
     * Attribute indicating that there is something loading for the `<TextField />`
     *
     * @default false
     */
    loading?: boolean;

    /**
     * Phrase to render as input control ‘help’ message
     */
    helperText?: string;

    /**
     * Always show ‘help’ message, even when control is not focused
     *
     * @default false
     */
    helperTextIsPersistent?: boolean;

    /**
     * Unique identifier for the `<TextField />` instance. You may pass your own value to override the component’s auto-generated ID.
     */
    id?: string;

    /*
     * The `inputmode` attribute provides a hint to the browser for which type of onscreen keyboard to display. Inputmode is unique to text-input and text-area controls.
     */
    inputMode?: INPUTMODE;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    inputProps?: { [prop: string]: any };

    /*
     * Required accessible label associated with this input control.
     */
    label: string;

    /**
     * The maximum number of characters that the user can enter
     */
    maxLength?: number;

    /**
     * Name of the `<input>`
     */
    name?: string;

    /*
     * Input validation pattern
     */
    pattern?: string;

    /**
     * Boolean attribute indicating the `<input>` should be required
     *
     * @default false
     */
    required?: boolean;

    /*
     * Input type (text, password, tel, etc.)
     */
    type?: INPUT_TYPES;

    /**
     * Value of the `<input>`
     */
    value?: string;

    /**
     * Validation status of the `<input>`
     */
    validationStatus?: 'valid' | 'invalid';

    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

export const TextField: FC<TextFieldProps> = (props) => {
    const {
        autoCapitalize = 'none',
        autoComplete = 'off',
        className,
        disabled = false,
        helperText,
        helperTextIsPersistent = false,
        id: initialId,
        inputMode = 'text',
        inputProps,
        validationStatus,
        label,
        maxLength,
        name,
        onBlur,
        onChange,
        onFocus,
        pattern,
        required = false,
        type = 'text',
        value,
        loading = false,
    } = props;

    const [id] = React.useState(initialId || nanoid(8));
    const textFieldId = `text-field-${id}`;
    const helperTextId = `text-field-helper-text-${id}`;
    const textFieldRef = React.useRef<HTMLInputElement>(null);
    const isFocused = useFocus(textFieldRef);

    useEffect(() => {
        const field = textFieldRef.current;
        if (field) {
            field.addEventListener(
                'blur',
                (event) => onBlur && onBlur((event as unknown) as React.FocusEvent<HTMLInputElement>)
            );
            field.addEventListener(
                'change',
                (event) => onChange && onChange((event as unknown) as React.ChangeEvent<HTMLInputElement>)
            );
        }
    }, []);

    useEffect(() => {
        const elRef = textFieldRef?.current;
        if (elRef) {
            if (isFocused && inputProps?.placeholder) {
                elRef.placeholder = inputProps?.placeholder;
            } else {
                elRef.placeholder = '';
            }
        }
    }, [isFocused]);

    return (
        <>
            <div
                className={classNames(className, 'mdc-text-field', {
                    'mdc-text-field--disabled': disabled,
                    'mdc-text-field--focused': isFocused,
                    'mdc-text-field--invalid': validationStatus === 'invalid',
                    'icon-right': validationStatus !== undefined || loading,
                })}
            >
                <input
                    aria-controls={helperText ? helperTextId : undefined}
                    aria-describedby={helperText ? helperTextId : undefined}
                    autoCapitalize={autoCapitalize}
                    autoComplete={autoComplete}
                    className="mdc-text-field__input"
                    disabled={disabled}
                    id={textFieldId}
                    inputMode={inputMode}
                    pattern={pattern}
                    maxLength={maxLength}
                    name={name}
                    ref={textFieldRef}
                    required={required}
                    type={type}
                    value={value}
                    onBlur={onBlur}
                    onChange={onChange}
                    onFocus={onFocus}
                    {...inputProps}
                />
                {validationStatus && (
                    <div className={`mdc-text-field-icon${'--' + validationStatus}`}>
                        {validationStatus === 'valid' ? (
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path d="M0 0h24v24H0z" fill="none"></path>
                                <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"></path>
                            </svg>
                        ) : (
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path d="M0 0h24v24H0z" fill="none"></path>
                                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"></path>
                            </svg>
                        )}
                    </div>
                )}
                {loading && (
                    <div style={{ textAlign: 'center', paddingTop: '300px' }}>
                        <hzn-loading tone="brand" label="loading content" size="large"></hzn-loading>
                    </div>
                )}
                <label
                    className={classNames('mdc-floating-label', {
                        'mdc-floating-label--float-above': isFocused || (value !== undefined && value !== ''),
                    })}
                    htmlFor={textFieldId}
                >
                    {label}
                </label>
                <div className={classNames('mdc-line-ripple', { 'mdc-line-ripple--active': isFocused })}></div>
            </div>
            {helperText ? (
                <div
                    className={classNames('mdc-text-field-helper-text', {
                        'mdc-text-field-helper-text--persistent': helperTextIsPersistent,
                        'mdc-text-field-helper-text--validation-msg': validationStatus === 'invalid',
                    })}
                    id={helperTextId}
                >
                    {helperText}
                </div>
            ) : null}
        </>
    );
};
